/**
 * Registration sidebar for OfflineMeeting.
 * Shows register/edit/waitlist button and opens a registration form modal.
 * Mirrors the kinasticCoach OfflineMeetingFormModal flow.
 */
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { OfflineMeetingEntry } from '../../../Model/Explore/OfflineMeetingEntry';
import { useSubscribedOfflineMeetingEvent, useWaitlistEntry } from '../../../Hooks/useSubscribedEvent';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { Theme } from '../../../Theme/Theme';
import { OfflineMeetingRegistrationModal } from './OfflineMeetingRegistrationModal';
import { useLocalized } from '../../../Hooks/useLocalized';
import { DurationBefore } from '../../../Model/Coach/Schedule/NotificationInterval';

const Header = styled.p`
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const StatusText = styled.p`
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 16px 0;
`;

const WaitlistBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #FFF8E1;
  border-radius: 10px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: #F57F17;
`;

const RegisterButton = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${Theme.Colors.primary};
  color: white;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditButton = styled(RegisterButton)`
  background: ${Theme.Colors.lightestGrey};
  color: ${Theme.Colors.textColor};
`;

export type ExploreContentViewOfflineMeetingRegistrationProps = {
  entry: OfflineMeetingEntry;
};

export const ExploreContentViewOfflineMeetingRegistration: React.FC<ExploreContentViewOfflineMeetingRegistrationProps> =
  observer(({ entry }) => {
    const { t } = useTrans();
    const { event, load: loadEvent } = useSubscribedOfflineMeetingEvent(entry.id);
    const { waitlist, load: loadWaitlist } = useWaitlistEntry(entry.id);
    const entryName = useLocalized(entry.name);

    const currentSubscription = event ?? waitlist;
    const isSubscribed = !!currentSubscription;
    const isOnWaitlist = !event && !!waitlist;
    const [showModal, setShowModal] = useState(false);

    const handleOpenModal = useCallback(() => setShowModal(true), []);
    const handleCloseModal = useCallback(() => setShowModal(false), []);

    const handleSaved = useCallback(() => {
      loadEvent();
      loadWaitlist();
      setShowModal(false);
    }, [loadEvent, loadWaitlist]);

    const handleUnregistered = useCallback(() => {
      loadEvent();
      loadWaitlist();
      setShowModal(false);
    }, [loadEvent, loadWaitlist]);

    const buildEventData = useCallback(() => {
      if (currentSubscription) {
        return { ...currentSubscription };
      }
      return {
        type: 'event.subscribed.offlineMeeting',
        objectType: entry.type,
        objectId: entry.id,
        duration: entry.duration,
        dates: entry.dates.map((d) => (d instanceof Date ? d.toISOString() : String(d))),
        notificationIntervals: [
          {
            title: '',
            message: '',
            durationBefore: DurationBefore.AT_EVENT,
            customData: {
              name: entryName,
              link: `https://coach.kinastic.com/explore/${entry.type}/${entry.id}`,
            },
          },
        ],
        registrationFields: entry.registrationFields.map((f) => ({
          identifier: f.identifier,
          label: f.label,
          value: f.value,
          minSize: f.minSize,
          maxSize: f.maxSize,
          valueType: f.valueType,
        })),
      };
    }, [currentSubscription, entry, entryName]);

    if (!entry.canSubscribe && !isSubscribed) {
      return (
        <div>
          <Header>
            {t('exploreModal.offlineMeeting.registrationClosed', {
              defaultValue: 'Registration closed',
            })}
          </Header>
          <StatusText>
            {t('exploreModal.offlineMeeting.registrationUnavailable', {
              defaultValue: 'Registration is no longer available for this meeting.',
            })}
          </StatusText>
        </div>
      );
    }

    const buttonText = isSubscribed
      ? t('exploreModal.offlineMeeting.editRegistrationInfo', {
          defaultValue: 'Edit Registration Info',
        })
      : entry.hasRemainingSeats
        ? t('exploreModal.offlineMeeting.register', { defaultValue: 'Register' })
        : t('exploreModal.offlineMeeting.registerOnWaitlist', {
            defaultValue: 'Put me on waiting list',
          });

    return (
      <div>
        <Header>
          {isSubscribed
            ? t('exploreModal.offlineMeeting.registeredTitle', {
                defaultValue: 'You are registered',
              })
            : t('exploreModal.offlineMeeting.registerTitle', { defaultValue: 'Registration' })}
        </Header>

        {isOnWaitlist && (
          <WaitlistBadge>
            <i className="bi bi-clock-history" />
            {t('exploreModal.offlineMeeting.waitListMessage', {
              defaultValue:
                'This meeting is full. You are on the waiting list and can still edit your info or unsubscribe.',
            })}
          </WaitlistBadge>
        )}

        {isSubscribed ? (
          <EditButton onClick={handleOpenModal}>{buttonText}</EditButton>
        ) : (
          <RegisterButton onClick={handleOpenModal}>{buttonText}</RegisterButton>
        )}

        <OfflineMeetingRegistrationModal
          visible={showModal}
          onClose={handleCloseModal}
          entry={entry}
          existingEvent={buildEventData()}
          isSubscribed={isSubscribed}
          onSave={handleSaved}
          onUnregister={handleUnregistered}
        />
      </div>
    );
  });
